'use client';

import type { PartialPaymentBounds } from '@/types/partial-payment';

export interface PartialPaymentAmountBlockProps {
  bounds: PartialPaymentBounds;
  currencyCode: string;
  currencySymbol: string;
  value: string;
  onChange: (value: string) => void;
  t: (key: string, fallback: string) => string;
  language: string;
  disabled?: boolean;
}

export default function PartialPaymentAmountBlock({
  bounds,
  currencyCode,
  currencySymbol,
  value,
  onChange,
  t,
  language,
  disabled = false,
}: PartialPaymentAmountBlockProps) {
  const num = parseFloat(value);
  const belowMin = value !== '' && !Number.isNaN(num) && num + 0.001 < bounds.min_amount;
  const aboveMax = value !== '' && !Number.isNaN(num) && num > bounds.max_amount + 0.001;

  const title =
    language === 'tr' ? 'Şimdi ödemek istediğiniz tutar' : 'Amount you want to pay now';
  const hint =
    language === 'tr'
      ? `Min ${bounds.min_amount.toFixed(2)} – max ${bounds.max_amount.toFixed(2)} ${currencySymbol} (para birimi: ${currencyCode})`
      : `Min ${bounds.min_amount.toFixed(2)} – max ${bounds.max_amount.toFixed(2)} ${currencySymbol} (currency: ${currencyCode})`;

  const loc = language === 'tr' ? 'tr-TR' : 'en-GB';
  const fmt = (n: number) =>
    n.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const remainingAfter =
    value !== '' && !Number.isNaN(num) && num > 0 && num <= bounds.max_amount + 0.001
      ? Math.max(0, Math.round((bounds.max_amount - num) * 100) / 100)
      : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5">
      <label className="mb-1 block text-sm font-bold text-alsero-dark-gray">{title}</label>
      <p className="mb-4 text-xs text-gray-600 leading-relaxed">{hint}</p>
      <div className="flex max-w-lg items-stretch gap-2">
        <span className="flex shrink-0 items-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700">
          {currencySymbol}
        </span>
        <input
          type="number"
          step="0.01"
          min={bounds.min_amount}
          max={bounds.max_amount}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-3 text-base font-medium tabular-nums focus:border-alsero-first focus:ring-2 focus:ring-alsero-first/20 disabled:opacity-60 min-h-[48px]"
        />
      </div>
      {remainingAfter != null && remainingAfter > 0.009 && (
        <p className="mt-3 text-xs text-gray-700">
          {language === 'tr' ? 'Kalan bakiye (taksit planına)' : 'Balance for payment schedule'}:{' '}
          <span className="font-semibold tabular-nums">
            {fmt(remainingAfter)} {currencySymbol}
          </span>
        </p>
      )}
      {belowMin && (
        <p className="mt-2 text-xs text-red-600">
          {t('payment.error.amount_below_min', `Minimum amount is ${bounds.min_amount.toFixed(2)}`)}
        </p>
      )}
      {aboveMax && (
        <p className="mt-2 text-xs text-red-600">
          {t('payment.error.amount_exceeds', 'Amount exceeds the maximum allowed')}
        </p>
      )}
    </div>
  );
}
